import {
  AirVent, AppWindow, BadgeCheck, Bandage, Bird, Biohazard, Blend, Blinds, BrickWall, Cable,
  ClipboardX,
  CloudRain, Columns3, Cone, DoorOpen, Drill, Droplets, EqualNot, Fan, Fence, FireExtinguisher,
  Flag, Footprints, Frame, Gauge, Grid2x2, Hammer, HardHat, House, Landmark, Layers, LayoutGrid,
  Leaf,
  LifeBuoy, ListChecks, Megaphone, MoveVertical, OctagonAlert, Package, PaintRoller, PhoneCall, Pickaxe,
  Recycle, RotateCcw, Rows3, ShieldAlert, Shovel, Siren, Snowflake, Sparkles,
  SprayCan, SquareStack, Sprout, Tag, Trees, TriangleAlert, Umbrella, Volume2, Waves, Wind,
  Wrench,
  Zap, UsersRound, Hourglass, Timer, TimerOff, Banknote, PackagePlus, PackageMinus,
  Boxes, Thermometer, Ruler, Weight, Ticket, Barcode, FileDigit,
} from "lucide-react";
import {
  resolvePropertyIcon,
  type PropertyIconDomain,
  type PropertyIconName,
  type PropertyIconTone,
} from "../../../shared/propertyIcons";

const BLUE = "var(--color-info, #0077C4)";
const GREEN = "var(--signal-green, #00B877)";
const VIOLET = "var(--property-violet, #8055B5)";
const ORANGE = "var(--property-due-soon, #C56824)";
const TEAL = "var(--property-teal, #16847E)";

/**
 * The tone names `propertyIcons` resolves, as colours.
 *
 * Every one is a `sega.css` token that the dark theme redefines, which is what
 * lets a HASHED tone be safe: a monogram can land on any of these without
 * anyone having checked that particular trade in dark mode.
 */
/**
 * Written WITHOUT the `var(--x, #fallback)` form the constants above use.
 * happy-dom drops a two-argument `var()` from an inline style silently, so a
 * mark coloured that way is untestable outside a browser — which is how the
 * observation families' colours came to be checked only in a sim run. These
 * tokens are all defined in `sega.css :root`, so the fallback bought nothing.
 */
const TONE: Record<PropertyIconTone, string> = {
  info: "var(--color-info)",
  green: "var(--signal-green)",
  violet: "var(--property-violet)",
  orange: "var(--property-due-soon)",
  teal: "var(--property-teal)",
  red: "var(--destructive)",
  amber: "var(--signal-amber)",
  neutral: "var(--muted-foreground)",
};

/**
 * Every icon a lexicon can name. Keyed on `PropertyIconName`, so adding a name
 * to the union without importing its icon is a type error rather than a blank
 * mark on a card.
 */
const PROPERTY_ICONS: Record<PropertyIconName, typeof Leaf> = {
  // Trade
  Layers, BrickWall, Columns3, Frame, Drill, Rows3,
  AppWindow, House, Umbrella, Blinds, Snowflake,
  SquareStack, PaintRoller, LayoutGrid, Footprints, Grid2x2, DoorOpen,
  Zap, Cable, Droplets, AirVent, Fan,
  FireExtinguisher, SprayCan, MoveVertical,
  Shovel, Trees, Cone, Fence, Pickaxe,
  HardHat, Package, Sparkles,
  // Observation type
  Leaf, BadgeCheck, Gauge, ShieldAlert, Hammer, Wrench,
  Wind, Landmark, Biohazard, Volume2, Waves, CloudRain, Sprout, Recycle, Bird,
  RotateCcw, ClipboardX, EqualNot, ListChecks,
  Bandage, TriangleAlert, LifeBuoy, OctagonAlert, Megaphone, Siren,
  PhoneCall, Blend,
  UsersRound, Hourglass, Timer, TimerOff, Banknote, PackagePlus, PackageMinus,
  Boxes, Thermometer, Ruler, Weight, Ticket, Barcode, FileDigit,
};

/**
 * One property value, drawn as the mark `resolvePropertyIcon` chose for it.
 *
 * Shared by the two OPEN-vocabulary properties — a company defines its own
 * trades AND its own observation types, so neither can be a lookup table and
 * both need the same monogram fallback. `propertyIcons.ts` does the deciding
 * and documents why; this only draws the answer.
 *
 * The monogram is a tinted tile rather than a glyph on purpose. It has to read
 * as "this value has no icon" and not as "this value's icon is two letters" —
 * a filled tile beside stroked icons is visibly a different KIND of mark, so
 * the run still parses at a glance.
 */
/**
 * What a property draws when it has NO value.
 *
 * Every other mark on the run has an unset state and draws it: the status ring
 * goes dashed and neutral, priority shows three unlit bars, a person becomes an
 * empty outline. These two drew a generic glyph for EVERY value, so they had an
 * unset state by accident — and resolving per value took it away, leaving an
 * empty chip as bare words whose left edge missed every chip beside it.
 *
 * `null` from the resolver ("there is no value to name") stays right. What to
 * DRAW for it is this layer's call: the glyph the property had before, in the
 * muted ink the word "Not set" is already set in.
 */
const PLACEHOLDER: Record<PropertyIconDomain, typeof Leaf> = {
  trade: Tag,
  observationType: Flag,
  fieldName: Gauge,
};

function PropertyValueMark({ value, domain, size = 16, muted = false }: { value: string; domain: PropertyIconDomain; size?: number; muted?: boolean }) {
  const resolved = resolvePropertyIcon(value, domain);
  // An unfamiliar measurement still gets a real measurement icon. Its field
  // name remains on the expanded chip and in the shorthand's accessible label.
  const mark = domain === "fieldName" && resolved?.kind !== "icon"
    ? { kind: "icon" as const, icon: "Gauge" as const, tone: "neutral" as const, family: undefined }
    : resolved;
  if (!mark) {
    const Unset = PLACEHOLDER[domain];
    return (
      <Unset
        data-property-placeholder={domain}
        {...(domain === "observationType" ? { "data-observation-family": "unset" } : {})}
        size={size}
        strokeWidth={2}
        aria-hidden="true"
        style={{ color: "var(--muted-foreground)", flex: "none" }}
      />
    );
  }
  const color = muted ? "var(--muted-foreground)" : TONE[mark.tone];
  /**
   * The family, for observation type only.
   *
   * `review-touchups.sim.mjs` reads this off every option in the type sheet
   * and asserts one colour and one shape per family, so it is a contract with
   * that suite rather than an internal. Trade has no family layer, and an
   * attribute naming one would be a lie on a trade chip.
   */
  const familyAttr = domain === "observationType"
    ? { "data-observation-family": (mark.kind === "icon" && mark.family) || "other" }
    : {};
  if (mark.kind === "monogram") {
    return (
      <span
        {...familyAttr}
        data-property-monogram={mark.initials}
        data-property-tone={mark.tone}
        aria-hidden="true"
        style={{
          flex: "none", width: 16, height: 16, borderRadius: 5,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          // 8.5px, not 9: two wide capitals ("WW") overflowed a 16px tile and
          // bled past its rounded corners. `overflow: hidden` is the backstop
          // for a pair wider still.
          fontSize: 8.5, fontWeight: 700, lineHeight: 1, letterSpacing: "-0.04em",
          overflow: "hidden",
          // Knocked out of a FILLED tile, the way `StatusMark` draws a settled
          // state. Tinting the letters with the same token as their background
          // put them at about 2.1:1 — and the monogram is the one mark whose
          // meaning is read rather than recognised.
          color: "var(--card)",
          background: color,
        }}
      >
        {mark.initials}
      </span>
    );
  }
  const Icon = PROPERTY_ICONS[mark.icon];
  return (
    <Icon
      {...familyAttr}
      data-property-icon={mark.icon}
      data-property-tone={muted ? "neutral" : mark.tone}
      size={size}
      strokeWidth={2}
      aria-hidden="true"
      style={{ color, flex: "none" }}
    />
  );
}

/** A trade's mark — the icon its vocabulary earned, or its initials. */
export function TradeMark({ value }: { value: string }) {
  return <PropertyValueMark value={value} domain="trade" />;
}

/**
 * An observation type's mark.
 *
 * Procore spells these "Category - Type", and this used to key off the
 * CATEGORY alone: all ten Environmental types drew one leaf, all six Safety
 * types one shield, and a company's own type drew a generic flag. The sub-type
 * now gets the shape and the family keeps the tone.
 */
export function ObservationTypeMark({ value }: { value: string }) {
  return <PropertyValueMark value={value} domain="observationType" />;
}

/** Resolve both built-in field keys and the label of a custom numeric question. */
export function FieldNameMark({ field, size = 16, empty = false }: { field: string; size?: number; empty?: boolean }) {
  return <PropertyValueMark value={field} domain="fieldName" size={size} muted={empty} />;
}

/** Stable field colors, including empty properties whose labels still need scanning. */
export function propertyColor(key = "", glyph = ""): string {
  const field = key.toLowerCase();
  if (/trade|type|resource|area|description|contents|comments?/.test(field)) return VIOLET;
  if (/time|hours|duration/.test(field) || glyph === "clock") return TEAL;
  if (/company|vendor|entity|deliveryfrom|workers|person|assignee|inspector|location|pin/.test(field) || glyph === "pin") return BLUE;
  if (/rate|quantity|number|cost|amount|temperature/.test(field) || glyph === "hash") return ORANGE;
  if (/issue|safety|calamity/.test(field)) return "var(--signal-amber, #C48A00)";
  return VIOLET;
}
